import React from 'react';
import { observer } from 'mobx-react';

import { Emphasis } from '@estee/elc-base-theme';

import {
    PriceItemWrapper,
    OldPrice,
    DiscountedPrice,
    Price,
    FreeItemLabel
} from '~views/ShoppingBagTab/CartSidebarItems/CartItem/CartItemPriceWrapper/CartItemPriceWrapperStyles';
import { diContainer } from '~setup/diContainer';
import { serviceNames } from '~setup/CartService';
import { classes } from '~utils/ClassNames';
import { formatPriceWithCurrency } from '~utils/PriceFormatter';

import IPriceItem from './IPriceItem';
import { useTranslation } from '~sharedComponents/Providers/Translations/TranslationsHook';

export const priceWrapperClass = classes('cart-item-price-wrapper');
export const priceClass = classes('cart-item-price');
export const discountedClass = classes('cart-item-price', 'discounted');
export const freeClass = classes('cart-item-free-price');
export const sampleClass = classes('cart-item-sample-price');
export const totalClass = classes('cart-item-total');
export const oldPriceClass = classes('cart-item-old-price');
export const percentClass = classes('cart-item-percent');
export const markDownClass = classes('cart-item-markdown-price');
export const donationClass = classes('cart-item-donation-label');
export const loyaltyNameClass = classes('cart-item-loyalty-name');
export const markDownDiscountedClass = 'elc-cart-item-markdown-discounted-price';

const maxDiscountAmount = 100;

const PriceItem: React.FC<IPriceItem> = observer(
    ({
        productDiscount,
        discountedPrice = 0,
        originalPrice,
        showDiscountedTotal,
        hasMarkDownPrice,
        isDonationItem,
        isFreeVirtualSkuItem,
        isLoyaltyReward,
        freeItem,
        freeSample,
        isWpOffer
    }) => {
        const { config } = diContainer.get(serviceNames.config);
        const { freeLabel, donationLabel, loyaltyName, productSampleLabel, complimentaryLabel } =
            useTranslation([
                'freeLabel',
                'donationLabel',
                'loyaltyName',
                'productSampleLabel',
                'complimentaryLabel'
            ]);

        const displayProductFreeLabel =
            config?.cartConfig?.productFreeLabel || config?.productFreeLabel;

        const hasOldPrice = originalPrice > discountedPrice;
        const hasFullDiscount = productDiscount === maxDiscountAmount;
        const showDiscounts =
            showDiscountedTotal && (hasOldPrice || !!productDiscount) && originalPrice > 0;

        const priceFormatted = formatPriceWithCurrency(originalPrice);
        const discountedFormattedPrice = formatPriceWithCurrency(discountedPrice ?? originalPrice);

        const discountedMarkDownItem = hasMarkDownPrice && showDiscounts;
        const discountedMarkDownItemClass = discountedMarkDownItem ? markDownDiscountedClass : '';
        const price = originalPrice === 0 && displayProductFreeLabel ? freeLabel : priceFormatted;

        const displayProductSampleLabel =
            config?.cartConfig?.productSampleLabel || config?.productSampleLabel;
        const isComplimentary = displayProductFreeLabel && freeSample && isWpOffer;

        const renderContent = () => {
            if (displayProductFreeLabel && freeItem) {
                return (
                    <FreeItemLabel className={freeClass} data-testid={totalClass}>
                        {complimentaryLabel}
                    </FreeItemLabel>
                );
            }

            if (isComplimentary || isFreeVirtualSkuItem) {
                return (
                    <FreeItemLabel className={sampleClass} data-testid={totalClass}>
                        {complimentaryLabel}
                    </FreeItemLabel>
                );
            }

            if (displayProductSampleLabel && freeSample) {
                return (
                    <FreeItemLabel className={sampleClass} data-testid={totalClass}>
                        {productSampleLabel}
                    </FreeItemLabel>
                );
            }

            if (showDiscounts) {
                if (hasFullDiscount && displayProductFreeLabel) {
                    return (
                        <FreeItemLabel className={discountedClass} data-testid={priceClass}>
                            {complimentaryLabel}
                        </FreeItemLabel>
                    );
                }

                return (
                    <>
                        <DiscountedPrice className={discountedClass} data-testid={priceClass}>
                            {discountedFormattedPrice}
                        </DiscountedPrice>
                        <OldPrice
                            className={oldPriceClass}
                            emphasis={Emphasis.medium}
                            data-testid={oldPriceClass}
                        >
                            {priceFormatted}
                        </OldPrice>
                    </>
                );
            }

            if (hasMarkDownPrice) {
                return (
                    <>
                        <DiscountedPrice
                            className={`${discountedClass} ${markDownClass}`}
                            data-testid={`${priceClass} ${markDownClass}`}
                        >
                            {discountedFormattedPrice}
                        </DiscountedPrice>
                        <OldPrice
                            className={`${oldPriceClass} ${markDownClass}`}
                            emphasis={Emphasis.medium}
                            data-testid={`${oldPriceClass} ${markDownClass}`}
                        >
                            {priceFormatted}
                        </OldPrice>
                    </>
                );
            }

            if (isDonationItem) {
                return (
                    <FreeItemLabel className={donationClass} data-testid={priceClass}>
                        {donationLabel}
                    </FreeItemLabel>
                );
            }

            if (isLoyaltyReward) {
                return <p className={loyaltyNameClass}>{loyaltyName}</p>;
            }

            return (
                <Price className={priceClass} data-testid={priceClass}>
                    {price}
                </Price>
            );
        };

        return (
            <PriceItemWrapper
                className={`${priceWrapperClass} ${discountedMarkDownItemClass}`}
                data-testid={priceWrapperClass}
            >
                {renderContent()}
            </PriceItemWrapper>
        );
    }
);

export default PriceItem;
